/**
 * Thema „Meine Rolle & was ich darf" (PV-037, A4). Die Tabelle wird nicht beschrieben, sondern aus
 * `rechteMatrix` (shared) berechnet — dieselbe Prüfung, die die Knöpfe sperrt.
 */
import {
  BEFUND_MATRIX_AKTIONEN,
  type RechteFall,
  type RechteZeile,
  SEGMENT_RECHTE,
  type WeiteresRechtEintrag,
  eigeneSegmente,
  rechteMatrix,
} from '@projektverwaltung/shared';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../../lib/features.js';
import { useSession } from '../../../lib/session.js';
import type { HilfeThemaProps } from './hilfe-typen.js';

export function ThemaRolle({ kompakt }: HilfeThemaProps) {
  const { t } = useTranslation();
  const { user } = useSession();
  const befundeAn = useFeatureFlag('befunde');
  if (!user) return null;

  const matrix = rechteMatrix(user);
  const ohneSegmente = eigeneSegmente(user).length === 0;
  // Befund-Aktionen nur bei Flag `befunde` — alles Sichtbare der Befunde hängt am Flag.
  const zeilen = befundeAn
    ? matrix.zeilen
    : matrix.zeilen.filter((z) => !BEFUND_MATRIX_AKTIONEN.includes(z.aktion));
  const mitFussnote = zeilen.some((z) => BEFUND_MATRIX_AKTIONEN.includes(z.aktion));
  const schrift = kompakt ? 'text-xs' : 'text-sm';

  const fallTitel = (f: RechteFall): string => {
    switch (f.art) {
      case 'segment':
        return t('hilfe.faelle.segment', { segment: f.segment });
      case 'service':
        return t('hilfe.faelle.service', { segment: f.segment });
      case 'eigen':
        return t('hilfe.faelle.eigen');
      case 'fremd':
        return ohneSegmente ? t('hilfe.faelle.beliebig') : t('hilfe.faelle.fremd');
    }
  };
  const zeilenTitel = (z: RechteZeile): string => {
    const basis = t(`hilfe.aktionen.${z.aktion}`);
    const mitEingang = z.eingang ? `${basis} – ${t('hilfe.faelle.eingang')}` : basis;
    return BEFUND_MATRIX_AKTIONEN.includes(z.aktion) ? `${mitEingang} *` : mitEingang;
  };
  const weiteresText = (w: WeiteresRechtEintrag): string => {
    const name = t(`hilfe.weitere.${w.recht}`);
    const titel = w.segment ? `${name} (${w.segment})` : name;
    if (!w.erlaubt) return `${titel}: ${t('hilfe.rolle.nein')}`;
    const alle = SEGMENT_RECHTE.includes(w.recht) && w.segment === null;
    return alle
      ? `${titel}: ${t('hilfe.rolle.ja')} (${t('hilfe.rolle.alle_segmente')})`
      : `${titel}: ${t('hilfe.rolle.ja')}`;
  };

  const rollen =
    user.function_roles.length === 0
      ? '—'
      : user.function_roles
          .map((r) =>
            r.scope_segment
              ? `${t(`rollen.${r.role}`)} (${r.scope_segment})`
              : t(`rollen.${r.role}`),
          )
          .join(' · ');

  return (
    <div className={`space-y-4 ${schrift}`} style={{ color: 'var(--ink-2)' }}>
      <div className="space-y-1">
        <p>
          <span className="font-medium" style={{ color: 'var(--ink)' }}>
            {t('hilfe.rolle.deine_rollen')}:
          </span>{' '}
          <span>{rollen}</span>
        </p>
        {user.is_admin && <p>{t('hilfe.rolle.admin')}</p>}
        {!user.is_admin && user.function_roles.length === 0 && <p>{t('hilfe.rolle.betrachter')}</p>}
        <p>{t('hilfe.rolle.pl_hinweis')}</p>
      </div>
      <p>{t('hilfe.rolle.einleitung')}</p>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th scope="col" className="py-1.5 pr-3 text-left font-medium">
                {t('hilfe.rolle.spalte_aktion')}
              </th>
              {matrix.faelle.map((f) => (
                <th
                  key={`${f.art}-${f.segment}`}
                  scope="col"
                  className="px-2 py-1.5 text-center font-medium"
                >
                  {fallTitel(f)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr
                key={`${z.aktion}-${z.eingang}`}
                className="border-t"
                style={{ borderColor: 'var(--line-soft)' }}
              >
                <th scope="row" className="py-1.5 pr-3 text-left font-normal">
                  {zeilenTitel(z)}
                </th>
                {matrix.faelle.map((f, i) => {
                  const erlaubt = z.werte[i] === true;
                  return (
                    <td
                      key={`${f.art}-${f.segment}`}
                      className="px-2 py-1.5 text-center"
                      style={{ color: erlaubt ? 'var(--success)' : 'var(--ink-3)' }}
                    >
                      {erlaubt ? t('hilfe.rolle.ja') : t('hilfe.rolle.nein')}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {mitFussnote && <p className="text-xs">* {t('hilfe.rolle.befund_fussnote')}</p>}
      <div className="space-y-1">
        <h3 className="font-medium" style={{ color: 'var(--ink)' }}>
          {t('hilfe.rolle.weitere_titel')}
        </h3>
        <ul aria-label={t('hilfe.rolle.weitere_titel')} className="space-y-0.5">
          {matrix.weitere.map((w) => (
            <li key={`${w.recht}-${w.segment ?? 'alle'}`}>{weiteresText(w)}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
